import { useUnsavedChanges, useConfirmLeave } from './UnsavedChanges';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, post, useData, useList, useSession, orgID } from '@yingbaba/api';
import {
  ActionDialog,
  Button,
  ButtonLink,
  Empty,
  ErrorNotice,
  Field,
  FileAttachment,
  Modal,
  Notice,
  PageTitle,
  Pagination,
  QueryState,
  Select,
  useToast,
} from '@yingbaba/ui';
import { Copy, Download, Plus, ArrowUpRight, Image as ImageIcon } from '@yingbaba/icons';
import { datetime, siteURL } from '@yingbaba/utils';
import type { Campaign, Project, ProjectFile, PublishingAccount } from '@yingbaba/types';
import { CampaignResults } from './CampaignResults';

interface Channel {
  name: string;
  creator_url: string;
  direct_publishing: boolean;
  guidance: string;
}
interface Kit {
  campaign: Campaign;
  account?: PublishingAccount;
  files: ProjectFile[];
  channel: Channel;
}
const channels = ['视频号', '抖音', '小红书', '哔哩哔哩', '其他'];
function ids(value: string) {
  try {
    const v = JSON.parse(value || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function PublishingAccountsPanel({ write }: { write: boolean }) {
  const session = useSession();
  const manage = ['owner', 'admin', 'manager'].includes(
    session.data?.organizations.find((o) => o.id === orgID())?.role || '',
  );
  const q = useData<PublishingAccount[]>('/publishing-accounts');
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null),
    [busy, setBusy] = useState('');
  const archivePending = useRef(false);
  return (
    <section className="panel">
      <div className="panel-title">
        <div>
          <h3>发布账号</h3>
          <p className="muted small">记录自己管理的账号，让每条内容明确发布到哪里。</p>
        </div>
        {write && (
          <ActionDialog
            title="添加发布账号"
            trigger="添加账号"
            path="/publishing-accounts"
            idempotent
            submitLabel="保存发布账号"
            fields={[
              { name: 'channel', label: '所属平台', options: channels },
              { name: 'name', label: '账号名称', max: 100 },
              {
                name: 'profile_url',
                label: '公开主页链接（可选）',
                type: 'url',
                required: false,
                max: 1000,
              },
            ]}
            description="填写你管理的账号名称和主页，方便安排每条内容的发布去向。"
          />
        )}
      </div>
      <ErrorNotice error={error} />
      <QueryState query={q}>
        {!q.data?.length && (
          <p className="muted">添加常用的视频号、抖音等账号，之后可在推广任务中选择。</p>
        )}
        <div className="publishing-account-grid">
          {q.data?.map((a) => (
            <article className="publishing-account-card" key={a.id}>
              <span className="tag">{a.channel}</span>
              <strong>{a.name}</strong>
              <span className="small muted">{a.status === 'active' ? '使用中' : '已停用'}</span>
              {a.profile_url && (
                <a className="link" href={a.profile_url} target="_blank" rel="noopener noreferrer">
                  公开主页
                  <ArrowUpRight size={13} />
                </a>
              )}
              {write &&
                a.status === 'active' &&
                (manage || a.created_by === session.data?.account.id) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={busy === a.id}
                    disabled={!!busy}
                    onClick={async () => {
                      if (archivePending.current) return;
                      archivePending.current = true;
                      setBusy(a.id);
                      setError(null);
                      try {
                        await post(`/publishing-accounts/${a.id}/archive`, {});
                        await qc.invalidateQueries();
                      } catch (e) {
                        setError(e);
                      } finally {
                        archivePending.current = false;
                        setBusy('');
                      }
                    }}
                  >
                    停用账号
                  </Button>
                )}
            </article>
          ))}
        </div>
      </QueryState>
    </section>
  );
}

function PublicationEditor({
  campaign,
  onClose,
  write,
}: {
  campaign: Campaign;
  onClose: () => void;
  write: boolean;
}) {
  const kit = useData<Kit>(`/campaigns/${campaign.id}/publication-kit`);
  const projects = useList<Project>('/workspace/projects?page_size=100');
  const accounts = useData<PublishingAccount[]>('/publishing-accounts');
  const [project, setProject] = useState(campaign.source_project_id || ''),
    [chosen, setChosen] = useState<string[]>(ids(campaign.media_file_ids)),
    [cover, setCover] = useState(campaign.cover_file_id || ''),
    [account, setAccount] = useState(campaign.publishing_account_id || ''),
    [rights, setRights] = useState(campaign.rights_confirmed || false);
  const [page, setPage] = useState(1),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  const [knownFiles, setKnownFiles] = useState<Record<string, ProjectFile>>({});
  const files = useList<ProjectFile>(`/projects/${project}/files?page=${page}`, !!project);
  const qc = useQueryClient(),
    toast = useToast();
  const editable = write && campaign.status === 'draft';
  useEffect(() => {
    const values = [...(kit.data?.files || []), ...(files.data?.items || [])];
    if (values.length)
      setKnownFiles((old) => ({ ...old, ...Object.fromEntries(values.map((f) => [f.id, f])) }));
  }, [kit.data?.files, files.data?.items]);
  const leave = useConfirmLeave();
  const dirty =
    editable &&
    JSON.stringify([project, [...chosen].sort(), cover, account, rights]) !==
      JSON.stringify([
        campaign.source_project_id || '',
        ids(campaign.media_file_ids).sort(),
        campaign.cover_file_id || '',
        campaign.publishing_account_id || '',
        campaign.rights_confirmed || false,
      ]);
  useUnsavedChanges({ dirty, busy, save, discard: onClose });
  const pending = useRef(false);
  async function save(): Promise<boolean> {
    if (pending.current) return false;
    if (project && !rights) {
      setError(new Error('请先确认所选素材的发布授权'));
      return false;
    }
    pending.current = true;
    setBusy(true);
    setError(null);
    try {
      await api(`/campaigns/${campaign.id}/media`, {
        method: 'PUT',
        body: JSON.stringify({
          project_id: project,
          file_ids: chosen,
          cover_file_id: cover,
          publishing_account_id: account,
          rights_confirmed: rights,
        }),
      });
      await qc.invalidateQueries();
      toast('素材与发布账号已保存');
      onClose();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(campaign.content);
      toast('发布文案已复制');
    } catch {
      setError(new Error('浏览器未允许复制，请直接选择文案复制。'));
    }
  }
  function exportChecklist() {
    const v = kit.data;
    if (!v) return;
    const content = [
      `# ${campaign.title}`,
      `平台：${campaign.channel}`,
      `账号：${v.account?.name || '待选择'}`,
      `计划：${campaign.scheduled_at ? datetime(campaign.scheduled_at) : '待安排'}`,
      '',
      '## 发布文案',
      campaign.content,
      '',
      '## 素材版本',
      ...v.files.map(
        (f) =>
          `- ${f.name} · V${f.version || 1}${f.id === campaign.cover_file_id ? ' · 封面' : ''}`,
      ),
      '',
      '## 发布前确认',
      '- [ ] 已核实人物肖像、音乐、商标与素材发布授权',
      '- [ ] 已核实目标平台账号与素材规格',
      '- [ ] 已核对标题、文案、字幕与封面',
      '- [ ] 发布后保存作品链接，并记录播放、互动等数据',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${campaign.title.replace(/[\\/:*?"<>|]/g, '_')}-发布清单.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const pinned = Object.values(knownFiles)
    .filter((f) => f.project_id === project && !files.data?.items.some((x) => x.id === f.id))
    .filter((f) => chosen.includes(f.id) || f.id === cover);
  const choices = [...(files.data?.items || []), ...pinned].filter(
    (f) => /^(image|video|audio)\//.test(f.mime) || /\.(srt|vtt)$/i.test(f.name),
  );
  return (
    <Modal
      title="素材与发布准备"
      className="promotion-preparation-dialog"
      description="整理本次发布要使用的素材、封面、账号和文案。"
      open
      busy={busy}
      onOpenChange={(open) => {
        if (!open && !busy) leave(onClose);
      }}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => leave(onClose)}>
            {editable ? '取消' : '关闭'}
          </Button>
          {editable && (
            <Button loading={busy} disabled={!!project && !rights} onClick={save}>
              保存素材与账号
            </Button>
          )}
        </>
      }
    >
      <ErrorNotice error={error || kit.error || projects.error || accounts.error} />
      <div className="promotion-preparation">
        <section>
          <span className="eyebrow">{campaign.channel}</span>
          <h3>{campaign.title}</h3>
          <p className="prose">{campaign.content}</p>
          <div className="row wrap">
            <Button variant="secondary" onClick={copy}>
              <Copy size={15} />
              复制发布文案
            </Button>
            <Button variant="secondary" disabled={!kit.data || dirty} onClick={exportChecklist}>
              <Download size={15} />
              下载发布清单
            </Button>
          </div>
          {dirty && <p className="small muted">保存素材与账号后，即可下载对应的发布清单。</p>}
          <p className="small muted">{kit.data?.channel.guidance}</p>
          {kit.data?.channel.creator_url && (
            <a
              className="btn btn-secondary"
              href={kit.data.channel.creator_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              前往官方创作中心
              <ArrowUpRight size={15} />
            </a>
          )}
        </section>
        <section className="stack">
          <Field label="发布账号">
            <Select value={account} disabled={!editable || busy} onChange={setAccount}>
              <option value="">暂不指定</option>
              {accounts.data
                ?.filter(
                  (a) =>
                    a.channel === campaign.channel && (a.status === 'active' || a.id === account),
                )
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.status !== 'active' ? '（已停用）' : ''}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="来源项目">
            <Select
              value={project}
              disabled={!editable || busy}
              onChange={(v) => {
                setProject(v);
                setChosen([]);
                setCover('');
                setRights(false);
                setPage(1);
              }}
            >
              <option value="">选择项目素材</option>
              {projects.data?.items.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </Select>
          </Field>
          {project && (
            <>
              <p className="small muted">
                选择本次发布要使用的素材。文件后续更新时，这里保留你选定的版本。
              </p>
              <ErrorNotice error={files.error} />
              {!files.isPending && !choices.length && !files.error && (
                <p className="muted small">
                  项目中还没有图片、视频、音频或字幕，可先前往项目上传。
                </p>
              )}
              {files.isPending && (
                <p className="muted small" role="status">
                  正在加载项目素材…
                </p>
              )}
              {!!choices.length && (
                <div className="campaign-media-picker">
                  {choices.map((f) => (
                    <label className="list-item" key={f.id}>
                      <input
                        type="checkbox"
                        disabled={
                          !editable || busy || (!chosen.includes(f.id) && chosen.length >= 12)
                        }
                        checked={chosen.includes(f.id)}
                        onChange={(e) =>
                          setChosen(
                            e.target.checked
                              ? [...chosen, f.id]
                              : chosen.filter((id) => id !== f.id),
                          )
                        }
                      />
                      <span>
                        <strong>{f.name}</strong>
                        <span className="small muted">
                          {' '}
                          V{f.version || 1}
                          {pinned.some((p) => p.id === f.id)
                            ? kit.data?.files.some((p) => p.id === f.id)
                              ? ' · 已关联版本'
                              : ' · 本次已选'
                            : ''}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <Pagination page={page} size={30} total={files.data?.total || 0} onPage={setPage} />
              <ButtonLink to={`/projects/${project}?tab=files`} variant="ghost">
                在项目中上传或整理素材
              </ButtonLink>
              <Field label="发布封面">
                <Select disabled={!editable || busy} value={cover} onChange={setCover}>
                  <option value="">不单独指定封面</option>
                  {choices
                    .filter((f) => f.mime.startsWith('image/'))
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name} · V{f.version || 1}
                      </option>
                    ))}
                </Select>
              </Field>
            </>
          )}
          {editable && (
            <label className="project-tool-consent">
              <input
                type="checkbox"
                checked={rights}
                disabled={busy}
                onChange={(e) => setRights(e.target.checked)}
              />
              我已取得素材及成片在所选渠道的发布授权
            </label>
          )}
        </section>
      </div>
      {Object.values(knownFiles).some(
        (f) => f.project_id === project && (chosen.includes(f.id) || f.id === cover),
      ) && (
        <section className="stack">
          <h3>本次选用的素材</h3>
          <p className="small muted">
            已选 {chosen.length} / 12 份发布素材{cover ? '，已指定封面' : ''}。可预览核对后保存。
          </p>
          {Object.values(knownFiles)
            .filter((f) => f.project_id === project && (chosen.includes(f.id) || f.id === cover))
            .map((f) => (
              <FileAttachment
                key={f.id}
                name={`${f.name} · V${f.version || 1}${f.id === cover ? ' · 封面' : ''}`}
                mime={f.mime}
                size={f.size}
                previewPath={`/files/${f.id}/preview`}
                downloadPath={`/files/${f.id}/download`}
              />
            ))}
        </section>
      )}
      <Notice>准备好素材和文案后，前往对应平台发布，再回来保存作品链接、跟进推广效果。</Notice>
    </Modal>
  );
}

export function PromotionStudio() {
  const [preparation, setPreparation] = useState<Campaign | null>(null);
  const [resetAfterCreate, setResetAfterCreate] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!resetAfterCreate) return;
    setResetAfterCreate(false);
    // Wait until the saved form's clean state has committed before changing
    // the URL, otherwise its leave guard can intercept our own page reset.
    if (params.has('page'))
      setParams(
        (p) => {
          p.delete('page');
          return p;
        },
        { replace: true },
      );
  }, [resetAfterCreate, params, setParams]);
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const query = params.get('q') || '';
  const [search, setSearch] = useState(query);
  useEffect(() => setSearch(query), [query]);
  const status = ['draft', 'scheduled', 'published'].includes(params.get('status') || '')
    ? params.get('status')!
    : '';
  const channel = channels.includes(params.get('channel') || '') ? params.get('channel')! : '';
  function filter(key: string, value: string) {
    setParams((p) => {
      value ? p.set(key, value) : p.delete(key);
      p.delete('page');
      return p;
    });
  }
  function setPage(value: number) {
    setParams((p) => {
      value > 1 ? p.set('page', String(value)) : p.delete('page');
      return p;
    });
  }
  const source = params.get('project') || '';
  const q = useList<Campaign>(
    `/workspace/campaigns?page=${page}&page_size=18&q=${encodeURIComponent(query)}&status=${status}&channel=${encodeURIComponent(channel)}&project=${encodeURIComponent(source)}`,
  );
  const projects = useList<Project>('/workspace/projects?page_size=100');
  const session = useSession();
  const write = ['owner', 'admin', 'manager', 'member'].includes(
    session.data?.organizations.find((o) => o.id === orgID())?.role || '',
  );
  return (
    <>
      <PageTitle
        title="视频推广工作台"
        description="从成片与封面，到发布文案、目标账号和效果记录。"
        action={
          write && (
            <ActionDialog
              title="新建推广任务"
              protectDraft
              trigger={
                <>
                  <Plus size={16} />
                  新建任务
                </>
              }
              path="/campaigns"
              idempotent
              submitLabel="创建推广草稿"
              fields={[
                { name: 'title', label: '任务名称', min: 2, max: 100 },
                { name: 'channel', label: '目标平台', options: channels },
                {
                  name: 'source_project_id',
                  label: '关联项目（可选）',
                  value: source,
                  required: false,
                  options: [
                    { value: '', label: '暂不关联项目' },
                    ...(projects.data?.items || []).map((p) => ({ value: p.id, label: p.title })),
                  ],
                },
                { name: 'content', label: '发布标题与文案', type: 'textarea', min: 5, max: 6000 },
                {
                  name: 'scheduled_at',
                  label: '计划发布时间',
                  type: 'datetime-local',
                  required: false,
                },
                {
                  name: 'rights_confirmed',
                  label: '素材授权',
                  required: false,
                  options: [
                    { value: '', label: '尚未关联项目素材' },
                    { value: 'yes', label: '已取得所选项目素材的发布授权' },
                  ],
                },
              ]}
              transform={(v) => ({
                ...v,
                rights_confirmed: v.rights_confirmed === 'yes',
                scheduled_at: v.scheduled_at ? new Date(v.scheduled_at).toISOString() : '',
              })}
              onSuccess={(v, { leaving }) => {
                setResetAfterCreate(true);
                if (!leaving) setPreparation(v as Campaign);
              }}
            />
          )
        }
      />
      <details className="publishing-account-settings">
        <summary>发布账号管理</summary>
        <PublishingAccountsPanel write={write} />
      </details>
      {source && (
        <div className="promotion-project-context">
          <span>
            项目：
            <Link className="link" to={`/projects/${source}`}>
              {projects.data?.items.find((p) => p.id === source)?.title || '当前项目'}
            </Link>
          </span>
          <Button size="sm" variant="ghost" onClick={() => filter('project', '')}>
            查看全部推广
          </Button>
        </div>
      )}
      <form
        className="promotion-list-filter"
        role="search"
        aria-label="查找推广任务"
        onSubmit={(e) => {
          e.preventDefault();
          filter('q', search.trim());
        }}
      >
        <input
          className="input"
          aria-label="推广关键词"
          placeholder="搜索任务名称或发布文案"
          maxLength={200}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select aria-label="推广状态" value={status} onChange={(v) => filter('status', v)}>
          <option value="">全部状态</option>
          <option value="draft">内容准备</option>
          <option value="scheduled">待发布</option>
          <option value="published">已发布</option>
        </Select>
        <Select aria-label="推广平台" value={channel} onChange={(v) => filter('channel', v)}>
          <option value="">全部平台</option>
          {channels.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Button type="submit" variant="secondary">
          搜索
        </Button>
      </form>
      <QueryState query={q}>
        {!q.data?.items.length && (
          <Empty
            title={query || status || channel ? '没有找到相关推广任务' : '为作品准备一次发布'}
            description={
              query || status || channel
                ? '试试其他关键词、状态或发布平台。'
                : '新建推广任务，整理素材和文案，再安排发布并跟进效果。'
            }
            action={
              query || status || channel || page > 1 ? (
                <Button
                  variant="secondary"
                  onClick={() => setParams(source ? { project: source } : {})}
                >
                  查看全部任务
                </Button>
              ) : undefined
            }
          />
        )}
        {!!q.data?.items.length && (
          <div className={`grid-3 promotion-board${status ? ' is-filtered' : ''}`}>
            {[
              ['draft', '内容准备'],
              ['scheduled', '待发布'],
              ['published', '已发布'],
            ]
              .filter(([value]) => !status || value === status)
              .map(([status, label]) => (
                <section
                  className={`promotion-column${q.data?.items.some((c) => c.status === status) ? '' : ' is-empty'}`}
                  key={status}
                >
                  <div className="row spread">
                    <h3>{label}</h3>
                    <span className="tag">
                      {q.data?.items.filter((c) => c.status === status).length || 0}
                    </span>
                  </div>
                  {!q.data?.items.some((c) => c.status === status) && !!q.data?.items.length && (
                    <p className="muted small">
                      {status === 'draft'
                        ? '暂无准备中的内容'
                        : status === 'scheduled'
                          ? '准备就绪的内容会出现在这里'
                          : '发布后保存作品链接，即可在这里查看'}
                    </p>
                  )}
                  {q.data?.items
                    .filter((c) => c.status === status)
                    .map((c) => (
                      <article className="panel promotion-task-card" key={c.id}>
                        <span className="tag">{c.channel}</span>
                        <h3>{c.title}</h3>
                        <p className="prose small">{c.content}</p>
                        <p className="small muted">
                          {c.scheduled_at ? datetime(c.scheduled_at) : '未安排时间'} ·{' '}
                          {ids(c.media_file_ids).length} 份素材
                        </p>
                        <div className="stack">
                          <Button variant="secondary" onClick={() => setPreparation(c)}>
                            <ImageIcon size={15} />
                            素材与发布准备
                          </Button>
                          {write && status === 'draft' && (
                            <>
                              <ActionDialog
                                title="编辑推广草稿"
                                protectDraft
                                trigger="编辑文案"
                              variant="secondary"
                                path={`/campaigns/${c.id}`}
                                method="PUT"
                                submitLabel="保存文案"
                                fields={[
                                  {
                                    name: 'title',
                                    label: '任务名称',
                                    value: c.title,
                                    min: 2,
                                    max: 100,
                                  },
                                  {
                                    name: 'channel',
                                    label: '目标平台',
                                    options: channels,
                                    value: c.channel,
                                  },
                                  {
                                    name: 'content',
                                    label: '发布文案',
                                    type: 'textarea',
                                    value: c.content,
                                    min: 5,
                                    max: 6000,
                                  },
                                  {
                                    name: 'scheduled_at',
                                    label: '计划发布时间',
                                    type: 'datetime-local',
                                    value: c.scheduled_at
                                      ? new Date(
                                          new Date(c.scheduled_at).getTime() -
                                            new Date().getTimezoneOffset() * 60000,
                                        )
                                          .toISOString()
                                          .slice(0, 16)
                                      : '',
                                    required: false,
                                  },
                                  {
                                    name: 'rights_confirmed',
                                    label: '发布授权',
                                    required: false,
                                    options: [
                                      { value: '', label: '不变更发布渠道' },
                                      { value: 'yes', label: '已重新核实新渠道的发布授权' },
                                    ],
                                  },
                                ]}
                                transform={(v) => ({
                                  ...v,
                                  rights_confirmed: v.rights_confirmed === 'yes',
                                  scheduled_at: v.scheduled_at
                                    ? new Date(v.scheduled_at).toISOString()
                                    : '',
                                })}
                              />
                              <ActionDialog
                                title="加入发布计划"
                                trigger="准备就绪"
                                path={`/campaigns/${c.id}/schedule`}
                                submitLabel="加入待发布"
                                description="核对账号、素材、文案和发布授权后，将任务加入待发布列表。"
                              />
                            </>
                          )}
                          {write && status === 'scheduled' && (
                            <>
                              <ActionDialog
                                title="退回草稿"
                                trigger="继续编辑"
                                variant="secondary"
                                path={`/campaigns/${c.id}/reopen`}
                                submitLabel="退回编辑"
                              />
                              <ActionDialog
                                title="记录已发布作品"
                                trigger="已在平台发布"
                                path={`/campaigns/${c.id}/publish`}
                                submitLabel="保存作品链接"
                                fields={[{ name: 'url', label: '作品链接', type: 'url' }]}
                                description="填写已在目标平台发布的作品链接，方便查看和跟进效果。"
                              />
                            </>
                          )}
                          {status === 'published' && (
                            <>
                              <a
                                className="btn btn-secondary"
                                href={c.published_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                查看已发布作品
                                <ArrowUpRight size={15} />
                              </a>
                              <CampaignResults campaign={c} write={write} />
                            </>
                          )}
                        </div>
                      </article>
                    ))}
                </section>
              ))}
          </div>
        )}
      </QueryState>
      <Pagination page={page} size={18} total={q.data?.total || 0} onPage={setPage} />
      {preparation && (
        <PublicationEditor
          key={preparation.id}
          campaign={preparation}
          onClose={() => setPreparation(null)}
          write={write}
        />
      )}
    </>
  );
}
